import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiMinus } = FiIcons;

const faqs = [
  {
    q: 'What kinds of companies is this for?',
    a: 'Operations and IT managers at asset-heavy companies with 50–300 employees. That typically means medical device companies, equipment rental businesses, field service operations, and logistics companies — teams that manage physical assets and internal support requests but are still doing it in spreadsheets and email.',
  },
  {
    q: 'What does the system actually track?',
    a: 'Physical assets — equipment, devices, vehicles, tools — including location, status, assigned user or site, maintenance history, and lifecycle stage. It also handles internal support requests, so your team has one place to submit issues and your managers have one place to see what is open.',
  },
  {
    q: 'Do we need to already be using Jira?',
    a: 'No. Many clients come to us with no Atlassian tooling in place. We handle the full setup from scratch. If you already have a Jira or JSM environment, we build on top of what exists.',
  },
  {
    q: 'How long does the build take?',
    a: 'Most implementations go live in 4–6 weeks from kickoff. The timeline depends on the number of asset types, how many sites or locations you operate, and whether there are integrations with existing systems.',
  },
  {
    q: 'What happens to our existing spreadsheet data?',
    a: 'We migrate it. As part of the build, we take your existing asset records and load them into JSM so you are not starting from a blank slate. The quality and structure of the migration depends on the quality of the source data.',
  },
  {
    q: 'What does the retainer cover?',
    a: 'Ongoing JSM admin and configuration work — adding new asset types, updating workflows, adjusting automations, onboarding new sites or teams, and handling configuration changes as your operation evolves. One active workstream at a time within bounded scope.',
  },
  {
    q: 'What is the AI voice intake add-on?',
    a: 'An optional AI voice agent that handles inbound calls from field techs or site managers. The agent captures the issue, links it to the right asset, creates a JSM ticket, and routes it to the correct queue — without any manual entry. It is scoped and priced separately after the core JSM build is confirmed.',
  },
  {
    q: 'Do you replace our internal IT person?',
    a: 'Not completely. The retainer is best as a specialist layer — someone who knows JSM deeply and handles the configuration and admin work your internal team does not have time or expertise for. It works alongside internal IT, not instead of it.',
  },
  {
    q: 'Can this connect to our other systems?',
    a: 'Integrations with existing tools are scoped during discovery. Common integrations include ERP systems, procurement platforms, and notification tools. Complexity and feasibility are confirmed before the build starts.',
  },
  {
    q: 'What is not included?',
    a: 'Custom software development, plugin development, large-scale migrations with poor source data, emergency or war-room support, and open-ended consulting. If a request falls outside the agreed scope, it gets scoped separately before work begins.',
  },
];

const HomeFAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" className="py-24" style={{ background: '#f0fdf4' }}>
      <div className="max-w-3xl mx-auto px-8 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#16a34a' }}>FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#052e16' }}>Common questions.</h2>
        </motion.div>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="rounded-lg border overflow-hidden" style={{ borderColor: '#bbf7d0' }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                className="w-full px-6 py-4 text-left flex items-center justify-between transition-colors"
                style={{ background: open === i ? '#ffffff' : '#ffffff' }}>
                <span className="text-sm font-semibold pr-4" style={{ color: '#052e16' }}>{faq.q}</span>
                <SafeIcon icon={open === i ? FiMinus : FiPlus} className="w-4 h-4 flex-shrink-0" style={{ color: '#16a34a' }} />
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <div className="px-6 pb-5 pt-2 text-sm leading-relaxed border-t"
                      style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeFAQ;